import type { GroupAccessItem } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { GroupFmt } from '@/i18n/messages/en/groups.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `groups` em português do Brasil. Declarado, nunca inferido, então uma chave que falte
 * quebra o `tsc` (PRD-27 LOC-011).
 *
 * O vocabulário é o do ADR-0018 e é obrigatório: *grupo* (nunca *time*, nunca *equipe*),
 * *base de conhecimento*, *agente*, *sistema conectado*, *pessoa*, *público*. As frases
 * fixadas pelo D-37 vêm da lista ratificada e não se negociam aqui: "Quem pode usar",
 * "Pertence a", "Só quem mantém", "Grupos específicos", "Todas as pessoas que entram",
 * "Ninguém pode usar", "Você gerencia", "Inclui você".
 *
 * ── Gênero ──
 *
 * Um grupo é masculino e uma base de conhecimento é feminina, e as duas coisas aparecem na
 * mesma lista. Onde o tipo do item chega até a frase, ele decide a concordância
 * (`detail.reach`); onde não chega — as quatro consequências de `deletion` — a frase foi
 * escrita com adjetivos invariáveis, em vez de escolher um gênero no escuro (D-21).
 */

const pick = (fmt: GroupFmt, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

const counted = (fmt: GroupFmt, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pick(fmt, count, forms)}`

const people = (fmt: GroupFmt, count: number): string => counted(fmt, count, { one: 'pessoa', other: 'pessoas' })

/**
 * O particípio concorda com o que foi compartilhado, e só o tipo do item sabe qual é.
 * Em inglês a mesma frase serve para os quatro; em português, não (D-21).
 */
const SHARED_WITH: Record<GroupAccessItem['kind'], string> = {
  knowledge_base: 'Compartilhada com',
  agent: 'Compartilhado com',
  connected_system: 'Compartilhado com',
  remote_agent: 'Compartilhado com'
}

export const groups: Messages['groups'] = {
  picker: {
    legend: 'Quem pode usar',
    kinds: {
      private: { label: (privateLabel) => privateLabel, hint: 'Só quem mantém' },
      groups: { label: 'Grupos específicos', hint: 'As pessoas dos grupos que você escolher' },
      everyone: { label: 'Todo mundo', hint: 'Todas as pessoas que entram' }
    },
    systemKinds: {
      everyone: { label: 'Todo mundo', hint: 'Todas as pessoas que entram' },
      groups: { label: 'Grupos específicos', hint: 'Só as pessoas dos grupos que você escolher' }
    },
    searchLabel: 'Encontrar um grupo',
    searchPlaceholder: 'Buscar grupos pelo nome',
    chosenLabel: 'Grupos escolhidos',
    sections: {
      manage: 'Você gerencia',
      member: 'Inclui você',
      other: 'Outros grupos'
    },
    allGroups: 'Todos os grupos',
    optionMeta: (fmt, count, manage, member) =>
      [people(fmt, count), manage ? 'você gerencia' : null, member ? 'inclui você' : null].filter(Boolean).join(' · '),
    // "com acesso" em vez de "compartilhado agora": a etiqueta tem 9px e o particípio pediria
    // o gênero do item, que esta marca não conhece.
    marks: {
      shared_now: 'com acesso',
      needs_review: 'precisa de revisão'
    },
    remove: (name) => `Remover ${name}`,
    results: (fmt, count) => counted(fmt, count, { one: 'grupo', other: 'grupos' }),
    noMatch: (q) => `Nenhum grupo se chama “${q}”.`,
    noGroups: 'Ainda não há grupos. Um administrador cria os grupos na página Grupos.',
    loading: 'Carregando os grupos…',
    failed: 'Não foi possível carregar os grupos. Tente de novo em instantes.',
    showList: 'Mostrar grupos',
    hideList: 'Ocultar grupos'
  },

  reach: (fmt, line, labels) => {
    switch (line.kind) {
      case 'pending':
        return 'Calculando quem isso alcança…'
      case 'no_groups':
        return 'Escolha pelo menos um grupo.'
      case 'private':
        return line.people === null ? labels.privateLabel : `${labels.privateLabel} · ${people(fmt, line.people)}`
      case 'everyone':
        return line.people === null
          ? 'Alcança todas as pessoas que entram.'
          : `Alcança todas as pessoas que entram — ${people(fmt, line.people)} hoje.`
      case 'groups': {
        if (line.people === null) return 'Calculando quem isso alcança…'
        const base = `Alcança ${people(fmt, line.people)}`
        if (line.delta === null) return `${base}.`
        if (line.delta === 0) return `${base} — o mesmo de agora.`
        if (line.delta > 0) return `${base} — ${fmt.formatCount(line.delta)} a mais do que agora.`
        return `${base} — ${fmt.formatCount(Math.abs(line.delta))} a menos do que agora. ${labels.consequence}`
      }
    }
  },

  review: (fmt, line, verb) => {
    const when = verb === 'publish' ? 'quando você publicar' : 'quando você salvar'
    switch (line.kind) {
      case 'none':
        return null
      case 'administrator':
        // "Você tem o papel de administrador" em vez de "Você é administrador": o papel é
        // masculino, a pessoa que o tem pode não ser.
        return `Você tem o papel de administrador, então isso entra no ar ${when}.`
      case 'live_now':
        return line.managedAdded.length > 0
          ? `Isso entra no ar ${when} — você gerencia ${fmt.formatList(line.managedAdded)}.`
          : `Isso entra no ar ${when}.`
      case 'everyone':
        return 'Compartilhar com todo mundo precisa da revisão de um administrador antes de entrar no ar.'
      case 'groups': {
        const waiting = pick(fmt, line.waiting.length, {
          one: `${line.waiting[0]} não é um grupo que você gerencia, então um administrador vai revisar a inclusão dele.`,
          other: `${fmt.formatList(line.waiting)} não são grupos que você gerencia, então um administrador vai revisar a inclusão deles.`
        })
        // Duas formas escritas, não uma reescrita em caixa alta: recapitalizar uma frase pronta
        // aqui também mexeria no NOME de um grupo (PRD-27 LOC-014).
        const leads = line.nowAdded.length === 0
        const removals = line.nowRemoved.map((name, index) =>
          leads && index === 0 ? `Remover ${name}` : `remover ${name}`
        )
        const now = [...line.nowAdded, ...removals]
        return now.length === 0
          ? waiting
          : `${waiting} ${fmt.formatList(now)} ${pick(fmt, now.length, { one: 'entra', other: 'entram' })} no ar agora.`
      }
    }
  },

  consequence: {
    agent: 'Essas pessoas perdem o acesso na próxima mensagem; as conversas delas continuam legíveis.',
    knowledge_base: 'Essas pessoas perdem o acesso na próxima busca ou mensagem.',
    system: 'Os agentes dessas pessoas param de usar na próxima mensagem.'
  },

  privateLabel: {
    me: 'Só eu',
    groupManagers: (group) => `Só quem gerencia ${group}`
  },

  page: {
    title: 'Grupos',
    lead: 'Pessoas que trabalham juntas. Compartilhe agentes e conhecimento com um grupo, e todo mundo nele pode usar a partir da próxima solicitação.',
    leadAdmin:
      'Crie grupos, nomeie quem gerencia cada um e veja a que cada grupo dá acesso. Quem gerencia adiciona e remove as pessoas.',
    newGroup: 'Novo grupo',
    searchLabel: 'Buscar grupos',
    searchPlaceholder: 'Buscar pelo nome',
    clearSearch: 'Limpar a busca',
    filters: { all: 'Todos os grupos', mine: 'Seus grupos', unmanaged: 'Sem gestão' },
    adminFilters: { all: 'Todos', mine: 'Seus', unmanaged: 'Sem gestão' },
    loading: 'Carregando os grupos…',
    failed: 'Não foi possível carregar os grupos.',
    failedBody: 'Tente de novo em instantes.',
    empty: 'Ainda não há grupos.',
    emptyBody: 'Um administrador cria os grupos e nomeia quem gerencia cada um.',
    emptyAdmin: 'Crie o primeiro grupo e depois nomeie quem vai gerenciá-lo — essas pessoas adicionam os membros.',
    emptyMine: 'Você ainda não está em nenhum grupo.',
    emptyMineBody: 'Quem gerencia um grupo adiciona as pessoas. A página de cada grupo diz quem o gerencia.',
    noMatch: (q) => `Nenhum grupo se chama “${q}”.`,
    noMatchBody: 'Confira a grafia ou procure em todos os grupos.',
    noManagersList: 'Sem gestão',
    managedBy: (fmt, names) => `Gerenciado por ${fmt.formatList(names)}`,
    rowMeta: (fmt, count, manage, member) =>
      [people(fmt, count), manage ? 'você gerencia' : null, member ? 'inclui você' : null].filter(Boolean).join(' · '),
    pickOne: 'Escolha um grupo para ver quem está nele e a que ele dá acesso.',
    back: 'Todos os grupos'
  },

  detail: {
    loading: 'Carregando o grupo…',
    missing: 'Este grupo não existe mais.',
    missingBody: 'Ele pode ter sido excluído. A lista mostra os grupos que existem agora.',
    failed: 'Não foi possível carregar este grupo.',
    failedBody: 'É um problema de leitura, não um grupo sem nada dentro. Tente de novo em instantes.',
    retry: 'Tentar novamente',
    membersFailed: 'Não foi possível carregar quem está nele. Tente de novo em instantes.',
    members: (fmt, count) => people(fmt, count),
    edit: 'Editar',
    delete: 'Excluir grupo…',
    toJoin: (fmt, names) => `Para entrar, fale com ${fmt.formatList(names)}.`,
    nobodyManages:
      'Ninguém gerencia este grupo ainda, então só um administrador pode adicionar pessoas a ele, e os administradores cuidam do que pertence a ele. Um administrador pode nomear quem vai gerenciá-lo.',
    sharedWith: (group) => `Compartilhado com ${group}`,
    sharedWithLead: (group) => `O que as pessoas de ${group} podem usar por estarem nele.`,
    sharedWithEmpty: (group) => `Nada foi compartilhado com ${group} ainda.`,
    ownedBy: (group) => `Pertence a ${group}`,
    ownedByLead: (group) =>
      `Mantido por quem gerencia ${group}. Estar no grupo não dá isso às pessoas por si só — só o compartilhamento dá.`,
    ownedByEmpty: (group) => `Nada pertence a ${group} ainda.`,
    kinds: {
      knowledge_base: 'Base de conhecimento',
      agent: 'Agente',
      connected_system: 'Sistema conectado',
      remote_agent: 'Agente remoto'
    },
    reach: (fmt, item) => {
      switch (item.reach) {
        case 'private':
          return 'Só quem mantém'
        case 'groups':
          return `${SHARED_WITH[item.kind]} ${counted(fmt, item.groupCount, { one: 'grupo', other: 'grupos' })}`
        case 'everyone':
          return 'Todo mundo'
        case 'nobody':
          return 'Ninguém pode usar'
      }
    },
    cannotOpen: 'Você não pode abrir isto',
    managers: 'Gestores',
    managersLead:
      'Essas pessoas adicionam e removem membros e cuidam do que pertence ao grupo. Os administradores as nomeiam.',
    noManagers: 'Ninguém gerencia este grupo ainda.',
    appoint: 'Nomear quem gerencia',
    removeManager: (name) => `Fazer ${name} parar de gerenciar este grupo`,
    managerNotMember: 'fora do grupo',
    members_: 'Membros',
    membersLead: 'Todo mundo aqui recebe o que é compartilhado com o grupo, a partir da próxima solicitação.',
    membersHidden: 'Só as pessoas deste grupo, quem o gerencia e os administradores veem quem está nele.',
    membersOnPeople: 'Você pode ver quem está nele, com o papel de cada pessoa, na página Pessoas.',
    openInPeople: 'Papéis e acesso',
    openInPeopleLabel: (group) => `Ver todo mundo em ${group} com o papel de cada pessoa, na página Pessoas`,
    addPeople: 'Adicionar pessoas',
    searchMembers: 'Buscar membros',
    searchMembersPlaceholder: 'Buscar por nome ou e-mail',
    noMembers: 'Ninguém está neste grupo ainda.',
    noMemberMatch: (q) => `Ninguém neste grupo corresponde a “${q}”.`,
    loadMore: 'Mostrar mais',
    removeMember: (name) => `Remover ${name}`,
    manages: 'gerencia',
    // "X adicionou" em vez de "Adicionado por X": o particípio concordaria com a pessoa que
    // entrou no grupo, e esta linha nunca sabe o gênero dela.
    addedBy: (name, when) => (name ? `${name} adicionou · ${when}` : `Entrou ${when}`),
    you: 'você'
  },

  create: {
    title: 'Novo grupo',
    lead: 'Um grupo é um conjunto de pessoas que trabalham juntas. Quem o gerencia adiciona os membros; você escolhe quem gerencia.',
    name: 'Nome',
    namePlaceholder: 'Financeiro EMEA',
    nameHint: 'Até 60 caracteres. As pessoas veem isso em todo lugar onde compartilham.',
    icon: 'Ícone',
    color: 'Cor',
    managers: 'Quem gerencia (opcional)',
    managersHint: 'Essas pessoas adicionam e removem membros. Você também pode nomear depois.',
    preview: 'Prévia',
    previewMembers: 'Nenhum membro ainda',
    submit: 'Criar grupo',
    submitting: 'Criando…',
    cancel: 'Cancelar',
    taken: 'Já existe um grupo com esse nome.'
  },

  edit: {
    title: (group) => `Editar ${group}`,
    lead: 'Renomear um grupo nunca muda quem pode usar o quê — tudo é compartilhado com o grupo, não com o nome dele.',
    submit: 'Salvar',
    submitting: 'Salvando…',
    stale: 'Outra pessoa mudou este grupo enquanto você editava. Feche e abra de novo para ver a alteração.'
  },

  colors: {
    slate: 'Ardósia',
    teal: 'Turquesa',
    plum: 'Ameixa',
    moss: 'Musgo',
    navy: 'Marinho',
    clay: 'Argila'
  },

  icons: {
    'users-round': 'Pessoas',
    handshake: 'Aperto de mão',
    'heart-handshake': 'Cuidado',
    'hand-heart': 'Apoio',
    network: 'Rede',
    puzzle: 'Quebra-cabeça',
    trophy: 'Troféu',
    compass: 'Bússola',
    'briefcase-business': 'Maleta',
    landmark: 'Instituição',
    scale: 'Balança',
    calculator: 'Calculadora',
    banknote: 'Cédula',
    'piggy-bank': 'Poupança',
    receipt: 'Recibo',
    'chart-line': 'Gráfico',
    megaphone: 'Megafone',
    newspaper: 'Jornal',
    mail: 'Correio',
    headset: 'Fone de ouvido',
    'life-buoy': 'Boia salva-vidas',
    store: 'Loja',
    'shopping-cart': 'Carrinho',
    presentation: 'Apresentação',
    'code-xml': 'Código',
    server: 'Servidor',
    cloud: 'Nuvem',
    cpu: 'Chip',
    microscope: 'Microscópio',
    atom: 'Átomo',
    dna: 'DNA',
    rocket: 'Foguete',
    factory: 'Fábrica',
    'hard-hat': 'Capacete',
    truck: 'Caminhão',
    package: 'Pacote',
    warehouse: 'Galpão',
    building: 'Prédio',
    'map-pin': 'Local',
    plane: 'Avião',
    'graduation-cap': 'Formatura',
    school: 'Escola',
    stethoscope: 'Estetoscópio',
    'heart-pulse': 'Saúde',
    leaf: 'Folha',
    sprout: 'Broto',
    palette: 'Paleta',
    coffee: 'Café'
  },

  iconRows: {
    groups: 'Grupos',
    business: 'Negócios',
    customers: 'Clientes',
    technology: 'Tecnologia',
    operations: 'Operações',
    people: 'Pessoas e cultura'
  },

  appoint: {
    title: (group) => `Nomear quem gerencia ${group}`,
    lead: (group) =>
      `Quem gerencia adiciona e remove as pessoas de ${group}. Pode compartilhar os próprios agentes e conhecimento com ele sem a revisão de um administrador, e cuida de tudo o que pertence a ${group}. Gerenciar um grupo não coloca a pessoa nele.`,
    person: 'Pessoa',
    submit: 'Nomear',
    submitting: 'Nomeando…'
  },

  add: {
    title: (group) => `Adicionar pessoas a ${group}`,
    lead: (fmt, group, bases, agents) =>
      `Elas recebem o acesso que ${group} dá — ${counted(fmt, bases, { one: 'base de conhecimento', other: 'bases de conhecimento' })} e ${counted(fmt, agents, { one: 'agente', other: 'agentes' })} — a partir da próxima solicitação.`,
    leadUnknown: (group) => `Elas recebem o acesso que ${group} dá a partir da próxima solicitação.`,
    submit: (fmt, count) =>
      count === 0 ? 'Adicionar' : `Adicionar ${counted(fmt, count, { one: 'pessoa', other: 'pessoas' })}`,
    submitting: 'Adicionando…',
    done: 'Pronto',
    // O particípio concorda com *pessoa*, por isso a frase inteira está dentro de cada forma.
    result: (fmt, added, already) =>
      [
        added === 0
          ? null
          : pick(fmt, added, {
              one: `${fmt.formatCount(added)} pessoa adicionada.`,
              other: `${fmt.formatCount(added)} pessoas adicionadas.`
            }),
        already === 0
          ? null
          : pick(fmt, already, {
              one: `${fmt.formatCount(already)} pessoa já estava no grupo.`,
              other: `${fmt.formatCount(already)} pessoas já estavam no grupo.`
            })
      ]
        .filter(Boolean)
        .join(' '),
    refused: {
      not_found: 'não tem mais uma conta aqui',
      deactivated: 'está com a conta desativada',
      service: 'é uma conta de serviço, não uma pessoa'
    }
  },

  remove: {
    title: (name, group) => `Remover ${name} de ${group}?`,
    body: (group) =>
      `Essa pessoa deixa de receber o acesso que ${group} dá a partir da próxima solicitação. As conversas dela continuam legíveis, e ela pode voltar ao grupo quando você quiser.`,
    submit: 'Remover',
    submitting: 'Removendo…'
  },

  removeManager: {
    title: (name, group) => `Fazer ${name} parar de gerenciar ${group}?`,
    body: (group) =>
      `Essa pessoa não vai mais poder adicionar ou remover pessoas, nem cuidar do que pertence a ${group}. Estar ou não no grupo não muda.`,
    submit: 'Parar de gerenciar',
    submitting: 'Atualizando…'
  },

  genericError: 'Algo deu errado. Tente de novo.',

  deletion: {
    title: (group) => `Excluir ${group}?`,
    lead: 'Isso não pode ser desfeito. Leia o que muda antes de confirmar.',
    loading: 'Calculando o que muda…',
    previewFailed:
      'Não foi possível calcular o que a exclusão mudaria, então ainda não dá para excluir. Tente de novo em instantes.',
    members: (fmt, count) =>
      count === 0
        ? 'Ninguém está nele, então ninguém perde nada.'
        : `${people(fmt, count)} ${pick(fmt, count, { one: 'deixa', other: 'deixam' })} de receber o acesso que ele dá.`,
    managers: (fmt, count) =>
      pick(fmt, count, {
        one: `${fmt.formatCount(count)} pessoa deixa de gerenciar o grupo.`,
        other: `${fmt.formatCount(count)} pessoas deixam de gerenciar o grupo.`
      }),
    sharedWith: 'Compartilhado com ele',
    // Adjetivos invariáveis: a mesma lista mistura bases (femininas) e agentes (masculinos).
    fallsBack: 'passa a ficar só com quem mantém',
    staysShared: 'continua disponível para os outros grupos',
    becomesUnusable: 'ninguém pode usar até um administrador escolher de novo',
    staysAvailable: 'continua disponível para os outros grupos',
    owns: 'Pertence a ele',
    ownsChoose: 'Pertence a ele — escolha um novo responsável',
    ownsLead: 'Tudo o que pertence ao grupo precisa de alguém responsável. Valem os limites de quem assumir.',
    requests: (fmt, sharing, lending) => {
      const parts = [
        sharing === 0
          ? null
          : counted(fmt, sharing, {
              one: 'solicitação de compartilhamento',
              other: 'solicitações de compartilhamento'
            }),
        lending === 0
          ? null
          : counted(fmt, lending, {
              one: 'solicitação para deixar um agente responder a partir de uma base que pertence a ele',
              other: 'solicitações para deixar um agente responder a partir de bases que pertencem a ele'
            })
      ].filter((part): part is string => part !== null)
      // O verbo concorda com quantas solicitações há ao todo, não com quantos trechos a lista
      // tem: "1 solicitação … vai ser encerrada", "3 solicitações … vão ser encerradas".
      return pick(fmt, sharing + lending, {
        one: `${fmt.formatList(parts)} vai ser encerrada, e quem pediu vai ser avisado.`,
        other: `${fmt.formatList(parts)} vão ser encerradas, e quem pediu vai ser avisado.`
      })
    },
    typeName: (group) => `Digite ${group} para confirmar`,
    submit: 'Excluir grupo',
    submitting: 'Excluindo…',
    drifted: 'Algo mudou enquanto você lia. As consequências abaixo estão atualizadas — leia de novo.',
    nothingShared: 'Nada foi compartilhado com ele.',
    nothingOwned: 'Nada pertence a ele.'
  },

  relation: {
    member: 'Você está neste grupo.',
    manager: 'Você gerencia este grupo, mas não está nele, então não recebe o que é compartilhado com ele.',
    member_and_manager: 'Você está neste grupo e o gerencia.',
    administrator: 'Você não está neste grupo. Com o papel de administrador, você pode ver e mudar tudo sobre ele.',
    outsider: 'Você não está neste grupo.'
  },

  ownerPicker: {
    legend: 'Pertence a',
    you: (name) => `Você (${name})`,
    youHint: 'Você mantém.',
    theyHint: 'Essa pessoa mantém.',
    groupHint: 'Quem gerencia o grupo mantém.',
    manage: 'você gerencia',
    anotherGroup: 'Outro grupo',
    anotherPerson: 'Outra pessoa',
    groupSearch: 'Encontrar um grupo',
    groupSearchPlaceholder: 'Buscar grupos pelo nome',
    noGroups: 'Nenhum grupo corresponde.',
    current: 'responsável atual'
  }
}
